// EMagazine.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ToastContainer } from 'react-toastify';
import { adsAPI, imagaeAPI } from '../../services/api';
import Header from './Header';
import Footer from './Footer';

import * as PDFJS from 'pdfjs-dist';
import ReactPageFlip from 'react-pageflip';
import 'react-toastify/dist/ReactToastify.css';

PDFJS.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS.version}/build/pdf.worker.min.mjs`;

const BATCH_SIZE = 5;
const BASE_RENDER_SCALE = 1.5; // High base rendering scale for standard screens

const PDFFlipBook = ({ pdfUrl }) => {
    const [pdfDoc, setPdfDoc] = useState(null); // Keeps PDF reference in memory for dynamic rendering
    const [pageImages, setPageImages] = useState([]);
    const [numPages, setNumPages] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [loadedCount, setLoadedCount] = useState(0);
    const [flipReady, setFlipReady] = useState(false);
    const [showThumbnails, setShowThumbnails] = useState(false);

    const flipRef = useRef(null);

    // 1. Core High-Quality Page Renderer
    const renderPageToDataURL = useCallback(async (pdf, pageNumber, zoomFactor = 1) => {
        try {
            const page = await pdf.getPage(pageNumber);
            
            // Adjust scale using current zoom setting AND device resolution (Retina/4K displays)
            const devicePixelRatio = window.devicePixelRatio || 1;
            const dynamicScale = BASE_RENDER_SCALE * zoomFactor * devicePixelRatio;

            const viewport = page.getViewport({ scale: dynamicScale });
            const canvas = document.createElement('canvas');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = false; // Prevents vector graphics/text blur

            await page.render({ canvasContext: ctx, viewport }).promise;
            return canvas.toDataURL('image/png');
        } catch (err) {
            console.error(`Error rendering page ${pageNumber}:`, err);
            return null;
        }
    }, []);

    // 2. Initial PDF Loading
    const loadPDF = useCallback(async (url) => {
        setIsLoading(true);
        setLoadError(null);
        setLoadedCount(0);
        setFlipReady(false);
        console.log('📄 Loading PDF from:', url);

        try {
            let pdf;
            try {
                pdf = await PDFJS.getDocument({ url }).promise;
                console.log('✅ Direct load succeeded');
            } catch (directError) {
                console.warn('⚠️ Direct load failed, trying fetch+blob:', directError);
                const response = await fetch(url, { mode: 'cors', credentials: 'include' });
                if (!response.ok) throw new Error(`HTTP ${response.status} – ${response.statusText}`);
                const arrayBuffer = await response.arrayBuffer();
                pdf = await PDFJS.getDocument({ data: arrayBuffer }).promise;
                console.log('✅ Blob load succeeded');
            }

            setPdfDoc(pdf);
            const total = pdf.numPages;
            setNumPages(total);
            setPageImages(new Array(total).fill(undefined));

            const images = [];
            for (let i = 1; i <= total; i += BATCH_SIZE) {
                const end = Math.min(i + BATCH_SIZE - 1, total);
                const batchPromises = [];
                for (let j = i; j <= end; j++) {
                    const dataUrlPromise = renderPageToDataURL(pdf, j, 1);
                    batchPromises.push({ index: j - 1, promise: dataUrlPromise });
                }

                const batchResults = await Promise.all(batchPromises.map(item => item.promise));
                batchPromises.forEach((item, idx) => {
                    images[item.index] = batchResults[idx];
                });

                setPageImages([...images]);
                setLoadedCount(images.filter(img => img !== undefined).length);
                await new Promise(resolve => setTimeout(resolve, 0));
            }

            setCurrentPage(0);
        } catch (error) {
            console.error('❌ PDF Load Error:', error);
            setLoadError(error.message || 'Failed to load PDF');
        } finally {
            setIsLoading(false);
        }
    }, [renderPageToDataURL]);

    useEffect(() => {
        if (pdfUrl) loadPDF(pdfUrl);
    }, [pdfUrl, loadPDF]);

    // 3. Dynamic Re-rendering loop when Zooming or Navigating Pages
    useEffect(() => {
        if (!pdfDoc || pageImages.length === 0) return;

        const reRenderVisiblePages = async () => {
            const pagesToUpdate = [];
            pagesToUpdate.push(currentPage);

            // Fetch active page indexes (facing left and right spreads)
            if (currentPage > 0) pagesToUpdate.push(currentPage - 1);
            if (currentPage < numPages - 1) pagesToUpdate.push(currentPage + 1);

            const updatedImages = [...pageImages];

            await Promise.all(
                pagesToUpdate.map(async (pageIdx) => {
                    const highResSrc = await renderPageToDataURL(pdfDoc, pageIdx + 1, zoom);
                    if (highResSrc) {
                        updatedImages[pageIdx] = highResSrc;
                    }
                })
            );

            setPageImages(updatedImages);
        };

        // Debounce slightly to prevent lagging while clicking buttons rapidly
        const delayTimer = setTimeout(() => {
            reRenderVisiblePages();
        }, 150);

        return () => clearTimeout(delayTimer);
    }, [zoom, currentPage, pdfDoc, numPages, renderPageToDataURL]);

    const goToPage = (index) => {
        if (flipRef.current && typeof flipRef.current.flip === 'function') {
            flipRef.current.flip(index);
        }
    };

    const nextPage = () => {
        if (flipRef.current && typeof flipRef.current.flipNext === 'function') {
            flipRef.current.flipNext();
        }
    };

    const prevPage = () => {
        if (flipRef.current && typeof flipRef.current.flipPrev === 'function') {
            flipRef.current.flipPrev();
        }
    };

    const zoomIn = () => setZoom((z) => Math.min(3, z + 0.5));
    const zoomOut = () => setZoom((z) => Math.max(1, z - 0.5));

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
    };

    const onFlip = useCallback((e) => setCurrentPage(e.data), []);
    const onInit = useCallback(() => setFlipReady(true), []);

    useEffect(() => {
        const handleKey = (e) => {
            if (!flipReady) return;
            if (e.key === 'ArrowRight') { e.preventDefault(); nextPage(); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); prevPage(); }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [flipReady]);

    if (isLoading && loadedCount === 0) {
        return (
            <div className="hz-loading-container">
                <div className="flipbook-loader" />
                <span>Preparing publication...</span>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="hz-error-container">
                <h4>⚠️ Failed to load Magazine</h4>
                <p>{loadError}</p>
                <button onClick={() => loadPDF(pdfUrl)} className="hz-retry-btn">Retry</button>
            </div>
        );
    }

    const allLoaded = pageImages.every(img => img !== undefined);

    return (
        <div className="heyzine-container">
            {/* Top Minimal Progress Bar */}
            {!allLoaded && (
                <div className="hz-progress-bar">
                    <div style={{ width: `${(loadedCount / numPages) * 100}%` }} className="hz-progress-fill" />
                </div>
            )}

            {/* Viewport container */}
            <div className="hz-viewport">
                <div
                    className="hz-zoom-wrap"
                    style={{
                        transform: `scale(${zoom})`,
                    }}
                >
                    <ReactPageFlip
                        ref={flipRef}
                        width={550}
                        height={750}
                        className="hz-flipbook"
                        onFlip={onFlip}
                        onInit={onInit}
                        showCover={true} // True allows natural single front page first, like real magazines
                        useMouseEvents={zoom === 1} // Disables swipe dragging when zoomed to allow pan-scrolling safely
                        startPage={0}
                        size="stretch"
                        flipOnClick={zoom === 1}
                        minWidth={315}
                        maxWidth={1000}
                        minHeight={420}
                        maxHeight={1350}
                        drawShadow={true}
                    >
                        {pageImages.map((src, index) => (
                            <div key={index} className="hz-page" data-density="hard">
                                {src ? (
                                    <div className="hz-page-content" style={{ backgroundImage: `url(${src})` }} />
                                ) : (
                                    <div className="hz-page-skeleton">Loading...</div>
                                )}
                            </div>
                        ))}
                    </ReactPageFlip>
                </div>
            </div>

            {/* Slide-out Thumbnail Panel */}
            <div className={`hz-thumbnail-drawer ${showThumbnails ? 'open' : ''}`}>
                <div className="hz-thumbnail-grid">
                    {pageImages.map((src, idx) => (
                        <div 
                            key={idx} 
                            onClick={() => src && goToPage(idx)} 
                            className={`hz-thumb-card ${idx === currentPage ? 'active' : ''}`}
                        >
                            {src ? <img src={src} alt="" /> : <div className="hz-thumb-placeholder">{idx + 1}</div>}
                            <span className="hz-thumb-number">{idx + 1}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modernized Heyzine-style Control Strip */}
            <div className="hz-toolbar">
                <div className="hz-toolbar-left">
                    <button onClick={() => setShowThumbnails(!showThumbnails)} className={`hz-icon-btn ${showThumbnails ? 'active' : ''}`} title="Thumbnails">
                        📖
                    </button>
                </div>

                <div className="hz-toolbar-center">
                    <button onClick={prevPage} disabled={currentPage === 0 || !flipReady} className="hz-icon-btn">
                        ⏮
                    </button>
                    <span className="hz-page-counter">
                        {currentPage === 0 ? 'Cover' : `${currentPage} - ${currentPage + 1}`} / {numPages}
                    </span>
                    <button onClick={nextPage} disabled={currentPage >= numPages - 1 || !flipReady} className="hz-icon-btn">
                        ⏭
                    </button>
                </div>

                <div className="hz-toolbar-right">
                    <button onClick={zoomOut} disabled={zoom <= 1} className="hz-icon-btn">➖</button>
                    <span className="hz-page-counter">{Math.round(zoom * 100)}%</span>
                    <button onClick={zoomIn} disabled={zoom >= 3} className="hz-icon-btn">➕</button>
                    <button onClick={toggleFullscreen} className="hz-icon-btn">⛶</button>
                </div>
            </div>

            {/* Embedded styles for beautiful page rendering */}
            <style>{`
                .heyzine-container {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    background: #111111;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    font-family: system-ui, -apple-system, sans-serif;
                }
                .hz-viewport {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    overflow: auto; /* Required to allow panning/scrolling during zoom */
                }
                .hz-zoom-wrap {
                    transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
                    transform-origin: center center;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .hz-flipbook {
                    box-shadow: 0 30px 70px rgba(0,0,0,0.7);
                }
                .hz-page {
                    background-color: #fff;
                    width: 100%;
                    height: 100%;
                }
                .hz-page-content {
                    width: 100%;
                    height: 100%;
                    background-size: contain;
                    background-repeat: no-repeat;
                    background-position: center;
                    box-shadow: inset 5px 0 25px rgba(0,0,0,0.08); /* Crease depth effect */
                }
                .hz-page-skeleton {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    background: #f5f5f5;
                    color: #999;
                }
                
                .hz-toolbar {
                    height: 56px;
                    background: rgba(20, 20, 20, 0.92);
                    backdrop-filter: blur(10px);
                    border-top: 1px solid rgba(255,255,255,0.05);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0 24px;
                    z-index: 10;
                }
                .hz-toolbar-left, .hz-toolbar-right, .hz-toolbar-center {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .hz-icon-btn {
                    background: transparent;
                    border: none;
                    color: #ccc;
                    font-size: 18px;
                    padding: 8px;
                    cursor: pointer;
                    border-radius: 6px;
                    transition: all 0.2s;
                }
                .hz-icon-btn:hover:not(:disabled) {
                    color: #fff;
                    background: rgba(255, 255, 255, 0.08);
                }
                .hz-icon-btn:disabled {
                    opacity: 0.25;
                    cursor: not-allowed;
                }
                .hz-icon-btn.active {
                    color: #4a90e2;
                    background: rgba(74, 144, 226, 0.15);
                }
                .hz-page-counter {
                    color: #aaa;
                    font-size: 13px;
                    font-weight: 500;
                    letter-spacing: 0.5px;
                }

                .hz-thumbnail-drawer {
                    position: absolute;
                    bottom: 56px;
                    left: 0;
                    width: 100%;
                    height: 130px;
                    background: rgba(15, 15, 15, 0.95);
                    border-top: 1px solid rgba(255,255,255,0.08);
                    transform: translateY(100%);
                    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 9;
                }
                .hz-thumbnail-drawer.open {
                    transform: translateY(0);
                }
                .hz-thumbnail-grid {
                    display: flex;
                    gap: 14px;
                    padding: 16px;
                    overflow-x: auto;
                    height: 100%;
                }
                .hz-thumb-card {
                    position: relative;
                    height: 90px;
                    flex-shrink: 0;
                    cursor: pointer;
                    border-radius: 4px;
                    border: 2px solid transparent;
                    overflow: hidden;
                    transition: all 0.2s;
                }
                .hz-thumb-card img {
                    height: 100%;
                    object-fit: cover;
                }
                .hz-thumb-card.active {
                    border-color: #4a90e2;
                    box-shadow: 0 0 10px rgba(74, 144, 226, 0.5);
                }
                .hz-thumb-number {
                    position: absolute;
                    bottom: 2px;
                    right: 4px;
                    background: rgba(0,0,0,0.6);
                    color: #fff;
                    font-size: 10px;
                    padding: 1px 4px;
                    border-radius: 3px;
                }

                .hz-progress-bar {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 3px;
                    background: #222;
                    z-index: 11;
                }
                .hz-progress-fill {
                    height: 100%;
                    background: #4a90e2;
                    transition: width 0.3s ease;
                }
                .hz-loading-container, .hz-error-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    background: #111;
                    color: #888;
                }
                .flipbook-loader {
                    width: 32px;
                    height: 32px;
                    border: 3px solid #333;
                    border-top-color: #4a90e2;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                    margin-bottom: 12px;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

// ---------- Main EMagazine Component (Fully integrated) ----------
const EMagazine = () => {
    const [loading, setLoading] = useState(false);
    const [ads, setAds] = useState([]);
    const [temperature, setTemperature] = useState('Loading...');

    const pdfUrl = `${process.env.PUBLIC_URL}/metro.pdf`;

    const fetchAds = async () => {
        setLoading(true);
        try {
            const res = await adsAPI.getAll();
            setAds(res.data.data);
        } catch (error) {
            console.error("Error fetching ads:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAds();
        fetch('https://wttr.in/Bengaluru?format=j1')
            .then(res => res.json())
            .then(data => setTemperature(data.current_condition[0].temp_C + '°C'))
            .catch(() => setTemperature('N/A'));
    }, []);

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            </div>
        );
    }

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <Header temperature={temperature} />
            <main style={{ backgroundColor: '#f9f9f9' }}>
                <section className="trending-posts-4__area trending-posts-4__space grey-bg section-space-bottom">
                    <div className="container custom-container">
                        <div className="trending-posts-4__top heading-display__wrap">
                            <div className="section-heading__wrap">
                                <h3 className="section__title">E-Magazine</h3>
                                <div className="section__border" />
                            </div>
                        </div>
                        <div className="row mb-minus-24">
                            <div className="col-lg-8">
                                <div className="trending-posts-4__wrapper mb-24" style={{
                                    height: "85vh",
                                    background: "#111",
                                    borderRadius: "12px",
                                    overflow: "hidden"
                                }}>
                                    <PDFFlipBook pdfUrl={pdfUrl} />
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <div className="blog-sidebar__wrapper">
                                    <div className="blog-sidebar__wrap mb-20">
                                        <h3 className="blog-sidebar__title">Advertisement</h3>
                                        <div className="blog-sidebar__gallery blog-sidebar__gallery-2">
                                            {ads.map((ad) => (
                                                <div key={ad.id}>
                                                    <img src={`${imagaeAPI}/uploads/ads/${ad.ads_photo}`} alt={ad.ads_title} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
};

export default EMagazine;